/**
 * PDF exporter (docs/opencadstudio-parity/02-plot-pdf-ctb.md tasks 2.5/2.6)
 * — pdf-lib port of the OCS pdf_export.rs drawing pipeline:
 *
 *   mm space (K = 2.834645 pt/mm) → placement translate → content rotation
 *   (0/90/180/270) → plot offset → drawing ops in millimetres, one content
 *   box clip per page, per-item clip polygons, dash arrays, CTB color /
 *   lineweight / screening resolution, TrueType embedding with a stroked
 *   outline fallback, plot stamp and optional multiply overprint.
 *
 * All geometry arrives in source units (metres); this module maps it onto
 * the sheet via the page's scale + origin.
 */

import {
    PDFDocument, PDFFont, PDFPage, PDFName, rgb, degrees, BlendMode,
    LineCapStyle, LineJoinStyle,
    pushGraphicsState, popGraphicsState, translate, rotateDegrees, scale,
    moveTo, lineTo, closePath, clip, endPath, fill,
    setLineWidth, setDashPattern, setStrokingColor, setFillingColor,
    setLineCap, setLineJoin, setGraphicsState,
    type PDFOperator, type RGB,
} from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { hexToRgb } from './aciColors';
import { resolveColor, resolveLineweight, resolveScreening } from './ctb';
import { strokeFontLines } from './plotStrokeFont';
import type { PlotItem, PlotPage, PlotStyleTable } from './types';

/** Millimetre → PDF point (postscript point = 1/72 inch). */
export const MM_TO_PT = 2.834645;

/** OCS lineweight floor: 0.1 pt. */
const MIN_LW_MM = 0.1 / MM_TO_PT;

export interface ExportOptions {
    /** Document display name for the plot stamp. */
    documentName: string;
    /** Effective scale label for the stamp ('1:100', 'Fit'). */
    stampScale: string;
}

export interface ExportedPlot {
    bytes: Uint8Array;
    warnings: string[];
    fontEmbedded: boolean;
}

/** Load + embed the bundled Noto Sans face; null → outline fallback. */
async function embedPlotFont(doc: PDFDocument): Promise<PDFFont | null> {
    try {
        doc.registerFontkit(fontkit);
        const base = import.meta.env?.BASE_URL ?? '/';
        const res = await fetch(`${base}fonts/NotoSans-Regular.ttf`);
        if (!res.ok) return null;
        const bytes = await res.arrayBuffer();
        return await doc.embedFont(bytes, { subset: true });
    } catch {
        return null;
    }
}

/** Register an ExtGState (opacity / blend) on a page, pdf-lib style. */
function embedGraphicsState(page: PDFPage, opacity: number, blend?: BlendMode): PDFName {
    const gs = page.doc.context.obj({
        Type: 'ExtGState',
        ca: opacity,
        CA: opacity,
        BM: blend,
    });
    return page.node.newExtGState('GS', gs);
}

/** '#rrggbb' → pdf-lib rgb, with the OCS screening blend toward white. */
function colorToPdf(hex: string, screening = 1): RGB {
    const c = hexToRgb(hex) ?? [0, 0, 0];
    const mix = (v: number) => (v / 255) * screening + (1 - screening);
    return rgb(mix(c[0]), mix(c[1]), mix(c[2]));
}

/** Content-box dimensions (unrotated, mm) for a rotation. */
function contentBox(page: PlotPage): { cw: number; ch: number } {
    return page.rotation === 90 || page.rotation === 270
        ? { cw: page.paperHeight, ch: page.paperWidth }
        : { cw: page.paperWidth, ch: page.paperHeight };
}

/** mm-space mapper: source units → sheet mm relative to the plot origin. */
function makeMapper(page: PlotPage): (x: number, y: number) => [number, number] {
    const s = page.scale > 0 ? page.scale : 1;
    return (x, y) => [(x - page.originX) * s, (y - page.originY) * s];
}

/** Map a clip polygon (source units) into mm space. */
function mapClip(clip: [number, number][] | null, map: (x: number, y: number) => [number, number]): [number, number][] | null {
    return clip && clip.length >= 3 ? clip.map(([x, y]) => map(x, y)) : null;
}

/** Push a clip polygon path (mm space) and cut. */
function pushClipPoly(poly: [number, number][]): PDFOperator[] {
    const ops: PDFOperator[] = [moveTo(poly[0][0], poly[0][1])];
    for (let i = 1; i < poly.length; i++) ops.push(lineTo(poly[i][0], poly[i][1]));
    ops.push(closePath(), clip(), endPath());
    return ops;
}

/** One wire stroke as operators (caller provides the resolved style). */
function wireOps(points: [number, number][], lwMm: number, dash: [number, number] | null, color: RGB): PDFOperator[] {
    const ops: PDFOperator[] = [
        setStrokingColor(color),
        setLineWidth(Math.max(MIN_LW_MM, lwMm)),
        setLineCap(LineCapStyle.Round),
        setLineJoin(LineJoinStyle.Round),
    ];
    if (dash && dash[0] > 0.02 && dash[1] > 0.02) ops.push(setDashPattern([dash[0], dash[1]], 0));
    ops.push(moveTo(points[0][0], points[0][1]));
    for (let i = 1; i < points.length; i++) ops.push(lineTo(points[i][0], points[i][1]));
    return ops;
}

/** Fill path: outer ring + holes (holes wound opposite for nonzero rule). */
function fillOps(ring: [number, number][], holes: [number, number][][], color: RGB): PDFOperator[] {
    const ops: PDFOperator[] = [setFillingColor(color)];
    const push = (poly: [number, number][]) => {
        ops.push(moveTo(poly[0][0], poly[0][1]));
        for (let i = 1; i < poly.length; i++) ops.push(lineTo(poly[i][0], poly[i][1]));
        ops.push(closePath());
    };
    push(ring);
    holes.forEach((hole) => push(hole.slice().reverse()));
    ops.push(fill());
    return ops;
}

export interface DrawPageDeps {
    doc: PDFDocument;
    font: PDFFont | null;
    styleTable: PlotStyleTable | null;
}

/**
 * Render one PlotPage into a fresh PDF page. `page.content` items are in
 * source units; the page's scale/origin/offset/rotation place them.
 */
function drawPlotPage(deps: DrawPageDeps, page: PlotPage, documentName: string, stampScale: string): void {
    const { cw, ch } = contentBox(page);
    const K = MM_TO_PT;
    const pdfPage = deps.doc.addPage([page.paperWidth * K, page.paperHeight * K]);
    const map = makeMapper(page);
    const ctb = deps.styleTable && !deps.styleTable.isStb ? deps.styleTable : null;

    // Placement: content box onto the sheet, rotated, then offset.
    const tx = page.rotation === 90 ? ch * K : page.rotation === 180 ? cw * K : 0;
    const ty = page.rotation === 180 ? ch * K : page.rotation === 270 ? cw * K : 0;
    const placement: PDFOperator[] = [
        pushGraphicsState(),
        translate(tx, ty),
        rotateDegrees(page.rotation),
        translate(page.offsetX * K, page.offsetY * K),
        scale(K, K),
        // Plot window: everything outside the content box is cut.
        moveTo(0, 0), lineTo(cw, 0), lineTo(cw, ch), lineTo(0, ch), closePath(), clip(), endPath(),
    ];
    pdfPage.pushOperators(...placement);

    let activeClip: [number, number][] | null = null;
    let clipOpen = false;

    const setClip = (poly: [number, number][] | null) => {
        if (samePoly(activeClip, poly)) return;
        if (clipOpen) pdfPage.pushOperators(popGraphicsState());
        clipOpen = false;
        activeClip = poly;
        if (poly) {
            pdfPage.pushOperators(pushGraphicsState(), ...pushClipPoly(poly));
            clipOpen = true;
        }
    };

    page.content.items.forEach((item) => {
        setClip(mapClip(item.clip, map));
        switch (item.kind) {
            case 'wire': drawWire(pdfPage, page, item, ctb, map); break;
            case 'fill': drawFill(pdfPage, item, ctb, map); break;
            case 'hatch': drawHatch(pdfPage, page, item, ctb, map); break;
            case 'text': drawText(pdfPage, item, ctb, map, deps.font); break;
        }
    });
    if (clipOpen) pdfPage.pushOperators(popGraphicsState());
    pdfPage.pushOperators(popGraphicsState());

    if (page.options.plotStamp) {
        drawStamp(pdfPage, deps.font, documentName, page, stampScale);
    }
}

function samePoly(a: [number, number][] | null, b: [number, number][] | null): boolean {
    if (a === b) return true;
    if (!a || !b || a.length !== b.length) return false;
    return a.every((p, i) => p[0] === b[i][0] && p[1] === b[i][1]);
}

/** CTB-resolved stroke style for an item: color + lineweight in mm. */
function resolveStroke(
    ctb: PlotStyleTable | null,
    aci: number,
    color: string,
    lineweightMm: number,
    options: PlotPage['options'],
    scaleMmPerUnit: number,
): { color: RGB; lw: number } {
    let colorHex = ctb ? (resolveColor(ctb, aci) ?? color) : color;
    let lw = ctb ? (resolveLineweight(ctb, aci) ?? (options.objectLineweights ? lineweightMm : 0.25)) : (options.objectLineweights ? lineweightMm : 0.25);
    if (options.scaleLineweights) lw *= scaleMmPerUnit / 1000;
    const screening = ctb ? resolveScreening(ctb, aci) : 1;
    return { color: colorToPdf(colorHex, screening), lw };
}

function drawWire(
    pdfPage: PDFPage,
    page: PlotPage,
    item: Extract<PlotItem, { kind: 'wire' }>,
    ctb: PlotStyleTable | null,
    map: (x: number, y: number) => [number, number],
): void {
    const { color, lw } = resolveStroke(ctb, item.aci, item.color, item.lineweight, page.options, page.scale);
    const dash = item.dash ? [item.dash[0] * page.scale, item.dash[1] * page.scale] as [number, number] : null;
    const opacity = page.options.plotTransparency ? item.opacity : 1;
    const blend = page.options.mergeLines ? BlendMode.Multiply : undefined;
    const ops: PDFOperator[] = [pushGraphicsState()];
    if (opacity < 1 || blend) ops.push(setGraphicsState(embedGraphicsState(pdfPage, opacity, blend)));
    ops.push(...wireOps(item.points.map(([x, y]) => map(x, y)), lw, dash, color));
    ops.push(popGraphicsState());
    pdfPage.pushOperators(...ops);
}

const FILL_STYLE_OPTIONS: PlotPage['options'] = {
    objectLineweights: true, scaleLineweights: false, plotTransparency: true,
    mergeLines: false, plotStamp: false, quality: 'medium',
};

function drawFill(
    pdfPage: PDFPage,
    item: Extract<PlotItem, { kind: 'fill' }>,
    ctb: PlotStyleTable | null,
    map: (x: number, y: number) => [number, number],
): void {
    const { color } = resolveStroke(ctb, item.aci, item.color, 0, FILL_STYLE_OPTIONS, 1);
    const opacity = item.role === 'wipeout' ? 1 : item.opacity;
    const ops: PDFOperator[] = [pushGraphicsState()];
    if (opacity < 1) ops.push(setGraphicsState(embedGraphicsState(pdfPage, opacity)));
    ops.push(...fillOps(item.ring.map(([x, y]) => map(x, y)), item.holes.map((h) => h.map(([x, y]) => map(x, y))), color));
    ops.push(popGraphicsState());
    pdfPage.pushOperators(...ops);
}

function drawHatch(
    pdfPage: PDFPage,
    page: PlotPage,
    item: Extract<PlotItem, { kind: 'hatch' }>,
    ctb: PlotStyleTable | null,
    map: (x: number, y: number) => [number, number],
): void {
    const { color, lw } = resolveStroke(ctb, item.aci, item.color, 0.18, page.options, page.scale);
    const opacity = page.options.plotTransparency ? item.opacity : 1;
    const ops: PDFOperator[] = [pushGraphicsState()];
    if (opacity < 1) ops.push(setGraphicsState(embedGraphicsState(pdfPage, opacity)));
    if (item.solid) {
        ops.push(...fillOps(item.ring.map(([x, y]) => map(x, y)), item.holes.map((h) => h.map(([x, y]) => map(x, y))), color));
    } else {
        ops.push(setStrokingColor(color), setLineWidth(Math.max(MIN_LW_MM, lw)), setLineCap(LineCapStyle.Round));
        item.segments.forEach((seg) => {
            const [a, b] = seg;
            ops.push(moveTo(...map(a[0], a[1])), lineTo(...map(b[0], b[1])));
        });
    }
    // Boundary always drawn (EntityLayer parity).
    const ringMm = item.ring.map(([x, y]) => map(x, y));
    ops.push(setStrokingColor(color), setLineWidth(Math.max(MIN_LW_MM, lw)));
    ops.push(moveTo(ringMm[0][0], ringMm[0][1]));
    for (let i = 1; i < ringMm.length; i++) ops.push(lineTo(ringMm[i][0], ringMm[i][1]));
    ops.push(popGraphicsState());
    pdfPage.pushOperators(...ops);
}

/** Text line layout: anchorY top parity (drei Text), 1.25 line advance. */
const LINE_ADVANCE = 1.25;
const ASCENT = 0.75;

function drawText(
    pdfPage: PDFPage,
    item: Extract<PlotItem, { kind: 'text' }>,
    ctb: PlotStyleTable | null,
    map: (x: number, y: number) => [number, number],
    font: PDFFont | null,
): void {
    const { color } = resolveStroke(ctb, item.aci, item.color, 0, FILL_STYLE_OPTIONS, 1);
    const opacity = item.opacity;
    const [x, y] = map(item.x, item.y);
    const lines = item.content.split('\n');
    if (font) {
        if (opacity < 1) pdfPage.pushOperators(pushGraphicsState(), setGraphicsState(embedGraphicsState(pdfPage, opacity)));
        lines.forEach((line, i) => {
            pdfPage.drawText(line, {
                x,
                y: y - (ASCENT + i * LINE_ADVANCE) * item.height,
                size: item.height,
                font,
                color,
                rotate: degrees(item.rotation),
                opacity,
            });
        });
        if (opacity < 1) pdfPage.pushOperators(popGraphicsState());
        return;
    }
    // Outline fallback: single-stroke vector font as wires.
    const ops: PDFOperator[] = [pushGraphicsState()];
    if (opacity < 1) ops.push(setGraphicsState(embedGraphicsState(pdfPage, opacity)));
    ops.push(setStrokingColor(color), setLineWidth(Math.max(MIN_LW_MM, 0.05)), setLineCap(LineCapStyle.Round), setLineJoin(LineJoinStyle.Round));
    lines.forEach((line, i) => {
        const ly = y - (ASCENT + i * LINE_ADVANCE) * item.height;
        strokeFontLines(line, x, ly, item.height, item.rotation).forEach((stroke) => {
            ops.push(moveTo(stroke[0][0], stroke[0][1]));
            for (let k = 1; k < stroke.length; k++) ops.push(lineTo(stroke[k][0], stroke[k][1]));
        });
    });
    ops.push(popGraphicsState());
    pdfPage.pushOperators(...ops);
}

/** WinAnsi-safe fold for the standard-font stamp. */
function sanitizeWinAnsi(text: string): string {
    const map: Record<string, string> = { ı: 'i', İ: 'I', ğ: 'g', Ğ: 'G', ş: 's', Ş: 'S', ü: 'u', Ü: 'U', ö: 'o', Ö: 'O', ç: 'c', Ç: 'C' };
    return text.replace(/[ıİğĞşŞüÜöÖçÇ]/g, (ch) => map[ch] ?? ch);
}

function drawStamp(
    pdfPage: PDFPage,
    font: PDFFont | null,
    documentName: string,
    page: PlotPage,
    stampScale: string,
): void {
    const text = `${documentName}  |  ${page.title}  |  ${stampScale}  |  ${new Date().toLocaleString()}`;
    const x = 8, y = 6, size = 7;
    if (font) {
        pdfPage.drawText(text, { x, y, size, font, color: rgb(0.35, 0.35, 0.35) });
        return;
    }
    try {
        pdfPage.drawText(sanitizeWinAnsi(text), { x, y, size, color: rgb(0.35, 0.35, 0.35) });
    } catch {
        // Encoding beyond WinAnsi — drop the stamp rather than fail the plot.
    }
}

/**
 * Render + save the whole document. Each PlotPage becomes one PDF page
 * (multi-layout PUBLISH: every sheet keeps its own setup and its own CTB
 * table, looked up from `tables` by plotStyleName).
 */
export async function exportPlotPages(
    pages: PlotPage[],
    tables: Map<string, PlotStyleTable>,
    deps: { documentName: string; stampScale: string },
): Promise<ExportedPlot> {
    const warnings: string[] = [];
    const doc = await PDFDocument.create();
    doc.setProducer('Yap CAD');
    doc.setCreator('Yap CAD plot pipeline');
    const font = await embedPlotFont(doc);
    if (!font) warnings.push('TrueType face unavailable — text rendered as stroked outlines.');

    pages.forEach((page) => {
        const table = page.plotStyleName ? tables.get(page.plotStyleName) ?? null : null;
        if (page.plotStyleName && !table) warnings.push(`Plot style "${page.plotStyleName}" not loaded — object colors used.`);
        if (table?.isStb) warnings.push(`"${page.plotStyleName}" is an STB table — named-style plotting is not supported.`);
        drawPlotPage({ doc, font, styleTable: table && !table.isStb ? table : null }, page, deps.documentName, deps.stampScale);
    });

    const bytes = await doc.save();
    return { bytes, warnings, fontEmbedded: !!font };
}
